
import _, { replace } from "lodash";
import rest from "@/rest";
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { Action, namespace } from "vuex-class";
import { List } from "linq-collections";

const globals = namespace("globals");

@Component({
    components: {}
})
export default class CompSpeak extends Vue {
  @Prop() guid!: string;
  @Prop() text!: string;
  @Prop() textArray!: string[];
  @Prop() alignment!: string;
  @Prop() size!: string; // 24, evtl.: x-small, small, medium, large, x-large
  @Prop() color!: string;
  @Prop() weight!: string; // lighter, normal, bold, bolder, 100,200,300,400,500,600,700,800,900
  @Prop() fontStyle!: string; // normal, italic, oblique
  @Prop() finalized!: boolean;
  @Prop() compSettings!: string;
  @Prop({ default: true }) accessibilityEnabled!: boolean; // New prop for accessibility features

  @globals.Getter selectedLanguageCode!: string | null;

  canSpeak = false;
  speaking = false;
  paused = false;
  synth: SpeechSynthesis | null = null;
  voices: SpeechSynthesisVoice[] | undefined = [];
  voiceList: any[] | null = null;
  selectedVoice: any | null = null; //= { name: "undefined", lang: "de-DE", voiceURI: "undefined", localService: false, default: false };
  pitch: number = 1.0;
  rate: number = 1.0;
  wordStart = 0;
  wordEnd = 0;
  settings: any = null;
  screenReaderAnnouncement = "";
  componentId = Math.random().toString(36).substr(2, 9);

  get accentColor() {
    try {
      const c = this.$store.state.ux.colorAccent;
      // console.log("Accent color from store:", c);
      return c || '#2196F3';
    } catch (e) {
      return '#2196F3';
    }
  }

  async mounted() {
    this.updateCompSettings();

    // https://stackoverflow.com/questions/33889107/speech-synthesis-in-chrome-for-android-not-loading-voices
    // Use window OR navigator as speechSynthesis may be exposed on navigator in some browsers
    this.canSpeak = ('speechSynthesis' in window) || (('speechSynthesis' in navigator) && (navigator as any).speechSynthesis != null);
    if (this.canSpeak) {
      this.synth = (window.speechSynthesis ?? (navigator as any).speechSynthesis) as SpeechSynthesis;
      if (this.synth) {
        (this.synth as any).onvoiceschanged = () => this.selectVoice();
      }
      await this.$globalHelper.delay(50);
      this.selectVoice();
    }

    // Add global keyboard listener for accessibility
    document.addEventListener('keydown', this.handleGlobalKeyDown);
  }

  beforeDestroy() {
    // Clean up global event listener
    document.removeEventListener('keydown', this.handleGlobalKeyDown);
    // Clean up speech synthesis handlers and stop any ongoing speech when component is destroyed
    if (this.synth) {
      try { (this.synth as any).onvoiceschanged = null; } catch (e) { /* ignore */ }
    }
    if (this.speaking) {
      this.onStopSpeaking();
    }
  }

  @Watch("compSettings")
  async onCompSettingsChange() {
    if (!this.compSettings) return;
    this.settings = JSON.parse(this.compSettings);
    this.pitch = this.settings.pitch ?? 1.0;
    this.rate = this.settings.rate ?? 1.0;
  }

  updateCompSettings() {
    if (!this.compSettings) return;
    this.settings = JSON.parse(this.compSettings);
    this.pitch = this.settings.pitch ?? 1.0;
    this.rate = this.settings.rate ?? 1.0;
  }

  handleGlobalKeyDown(event: KeyboardEvent) {
    // Allow escape key to stop speech globally
    if (event.key === 'Escape' && this.speaking) {
      this.onStopSpeaking();
      event.preventDefault();
    }
  }

  get textDecoded() {
    if (this.text != null)
        return this.decodeHTMLEntities(this.text);
    if (this.textArray != null)
        return this.decodeHTMLEntities(this.textArray.join(''));

    return "";
  }

  decodeHTMLEntities(text) {
    const txt = document.createElement('textarea');
    txt.innerHTML = text;
    return txt.value;
  }

  // getGermanVoices() {
  //   if (!this.synth) return;
  //   this.voices = this.synth.getVoices().filter(v => {
  //     return v.lang.replace('_', '-').includes("de-DE")
  //   });

  //   this.voiceList = new List(this.voices).select(v => <any>{
  //     name: v.name, uri: v.voiceURI, lang: v.lang.replace("_", "-"), default: v.default, local: v.localService
  //   }).toArray();
  // }

  // onSelectVoice(voice) {
  //   this.selectedVoice = voice;
  //   console.log("Selected voice: " + this.selectedVoice!.name);
  // }

  getVoicesForSelectedLanguage() {
    if (!this.synth) return;

    this.voices = [];

    // If a specific language is selected, filter voices for that language
    if (this.selectedLanguageCode) {
      // Take all voices and replace _ with - in lang for better matching with language codes (e.g. de-DE, en-US)
      this.voices = this.synth.getVoices().filter(v => v.lang.replace('_', '-').includes(this.selectedLanguageCode!));
    };

    // If no specific voices found for selected language, fallback to default language
    // if (this.voices.length === 0) {
    //   this.voices = this.synth.getVoices().filter(v => {
    //     return v.default === true;
    //   });
    // }

    // If no default voices, fallback to all available voices
    if (!this.voices || this.voices.length == 0) {
      this.voices = this.synth.getVoices();
    }

    // Create voice list for dropdown with relevant info
    this.voiceList = new List(this.voices)
    .where(v => v.localService === true)
    .select(v => <any>{
      name: v.name, uri: v.voiceURI, lang: v.lang.replace("_", "-"), default: v.default, local: v.localService
    }).toArray();
  }

  selectVoice() {
    this.getVoicesForSelectedLanguage();
    if (!this.voiceList || this.voiceList.length == 0) {
      this.selectedVoice = null;
      this.canSpeak = false;
      return;
    }

    // if (this.voiceList && this.voiceList.length) {
    //   console.log("Voices:");
    //   this.voiceList.forEach((voice: any, index: number) => {
    //   const voiceStr = `Voice ${index}: name=${voice.name ?? ''}, lang=${voice.lang ?? ''}, default=${voice.default ? 'true' : 'false'}, local=${voice.local ?? voice.localService ? 'true' : 'false'}`;
    //   console.log(voiceStr);
    //   });
    // } else {
    //   console.log("Keine Stimmen gefunden");
    // }

    this.selectedVoice = new List(this.voiceList).firstOrDefault(v => v.default && v.local && (v.name as string).toLowerCase().includes("microsoft"));
    if (!this.selectedVoice)
      this.selectedVoice = new List(this.voiceList).firstOrDefault(v => v.local && (v.name as string).toLowerCase().includes("microsoft"));
    if (!this.selectedVoice)
      this.selectedVoice = new List(this.voiceList).firstOrDefault(v => (v.name as string).toLowerCase().includes("microsoft"));
    if (!this.selectedVoice)
      this.selectedVoice = new List(this.voiceList).firstOrDefault(v => v.local);
    if (!this.selectedVoice)
      this.selectedVoice = new List(this.voiceList).firstOrDefault() ?? null;

    if (!this.selectedVoice) {
      this.canSpeak = false;
      return;
    }

    // console.log("Selected voice: " + this.selectedVoice!.name);
    this.canSpeak = true;
  }

  async onSelectVoice(voice: any) {
    this.selectedVoice = voice;
    this.announceToScreenReader(`Stimme ausgewählt: ${voice.name}`);
    // console.log("Voice selected:", voice.name);
    if (this.speaking) {
      this.onStopSpeaking();
      await this.$globalHelper.delay(250);
      this.onSpeak();
    }
  }

  // onChanged() {
  //   console.log("Selected voice: " + this.selectedVoice!.name);
  // }

  async onSpeak() {
    // Workaround for Edge on Android which supports speech synthesis but has a bug that prevents it from working properly.
    // There are no available voices reported from the API from Edge on Android, but it can still speak without voice selection, so we allow speaking but show a warning icon in the voice selection button.
    if (!this.canSpeak && !this.$globalHelper.isEdgeOnAndroid)
      return;

    // Check if speech API is currently used by another instance of CompSpeak
    if (this.synth && this.synth.speaking) {
      this.synth.cancel();
      this.wordStart = 0;
      this.wordEnd = 0;
      this.speaking = false;
      this.paused = false;
      await this.$globalHelper.delay(250);
    }

    // Announce to screen readers
    this.announceToScreenReader("Vorlesen gestartet");

    let utterance = new SpeechSynthesisUtterance();
    utterance.text = this.textDecoded;
    utterance.voice = this.voices!.find(v => v.voiceURI == this.selectedVoice.uri) ?? null;
    let lang = this.selectedVoice?.lang ?? this.selectedLanguageCode ?? 'de-DE';
    utterance.lang = lang; // Needed for Android to prevent unspecified behavior
    utterance.pitch = this.pitch;
    utterance.rate = this.rate;
    //utterance.volume = 1;
    // utterance.addEventListener('end', handleEnd);
    utterance.onend = () => this.endOfPlaying();
    utterance.onboundary = (e) => this.onBoundary(e);
    if (this.synth) {
      this.synth.speak(utterance);
    }
    this.speaking = true;
  }

  onBoundary(e: SpeechSynthesisEvent) {
    // console.log(e.name);
    if (e.name != "word")
      return;

    this.wordStart = e.charIndex;
    let wordLength = e.charLength;
    // iOS workaround
    if (wordLength == undefined) {
      let end = this.textDecoded.indexOf(" ", this.wordStart);
      if (end > 0)
        this.wordEnd = end;
      else
        this.wordEnd = this.textDecoded.length;
      return;
    }

    this.wordEnd = this.wordStart + wordLength;
  }

  get displayText() {
    if (this.wordStart == this.wordEnd)
      return this.textDecoded;

    let word = this.textDecoded.substring(this.wordStart, this.wordEnd);
    const markBg = this.getAccentWithAlpha();
    let markedText = this.textDecoded.substring(0, this.wordStart) +
      '<mark style="background-color: ' + markBg + ';" aria-label="Aktuell vorgelesenes Wort">' +
      word +
      '</mark>' +
      this.textDecoded.substring(this.wordEnd);

    return markedText;
  }

  getAccentWithAlpha() {
    const alphaHex = '70'; // ~0.82 opacity
    let c = (this as any).$store?.state?.ux?.colorAccent ?? this.accentColor ?? '#63a2cf';
    if (typeof c !== 'string') c = String(c);
    c = c.trim();
    try {
      if (c.startsWith('#')) {
        let hex = c.substring(1);
        if (hex.length === 3) {
          hex = hex.split('').map(ch => ch + ch).join('');
        }
        if (hex.length === 6) {
          return '#' + hex + alphaHex;
        }
        // if already contains alpha or unexpected length, fallback to rgba
      }
      if (c.startsWith('rgb')) {
        // handle rgb(...) or rgba(...)
        const nums = c.replace(/rgba?\(|\)|\s/g, '').split(',').map(s => parseFloat(s));
        if (nums.length >= 3) {
          const r = Math.round(nums[0]);
          const g = Math.round(nums[1]);
          const b = Math.round(nums[2]);
          const a = 208 / 255; // D0
          return `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
        }
      }
    } catch (e) {
      // fallthrough to default
    }
    // default fallback
    return '#63a2cf' + alphaHex;
  }

  get hideText() {
    if (this.settings?.hideText)
      return this.settings.hideText;

    return false;
  }

  get dense() {
    if (this.settings?.dense)
      return this.settings.dense;

    return false;
  }

  // get pitch() {
  //   if (this.settings?.pitch)
  //     return this.settings.pitch;

  //   return 1.2;
  // }

  // get rate() {
  //   if (this.settings?.rate)
  //     return this.settings.rate;

  //   return 1.0;
  // }

  endOfPlaying() {
    if (this.synth) {
      this.synth.cancel();
    }
    this.wordStart = 0;
    this.wordEnd = 0;
    this.speaking = false;
    this.paused = false;
    this.announceToScreenReader("Vorlesen beendet");
  }

  onPauseSpeaking() {
    if (this.synth) {
      this.synth.pause();
    }
    this.paused = true;
    this.announceToScreenReader("Vorlesen pausiert");
  }

  onResumeSpeaking() {
    if (this.synth) {
      this.synth.resume();
    }
    this.paused = false;
    this.announceToScreenReader("Vorlesen fortgesetzt");
  }

  async onAdaptVoiceChange() {
    if (this.speaking) {
      this.onStopSpeaking();
      // this.onPauseSpeaking();
      await this.$globalHelper.delay(250);
      this.onSpeak();
      // this.onResumeSpeaking();
    }
  }

  onStopSpeaking() {
    if (this.synth) {
      this.synth.cancel();
    }
    this.wordStart = 0;
    this.wordEnd = 0;
    this.speaking = false;
    this.paused = false;
    this.announceToScreenReader("Vorlesen gestoppt");
  }

  announceToScreenReader(message: string) {
    this.screenReaderAnnouncement = message;
    // Clear the announcement after a brief delay
    setTimeout(() => {
      this.screenReaderAnnouncement = "";
    }, 1000);
  }

  get fontSize() {
    if (this.size == null)
        return "18px";
    if (isNaN(Number(this.size))) // Number(this.size) != NaN)
        return this.size;
    return this.size + "px";
  }

  get lineHeight() {
    let fs = this.fontSize.replace("px", "");
    if (isNaN(Number(fs)))
        return this.fontSize;

    return Number(fs) + 2 + "px";
  }

  // get hyperText() {
  //   if (this.text != null)
  //       return this.text;
  //   if (this.textArray != null)
  //       return this.textArray.join('');

  //   return null;
  // }

  // Accessibility computed properties
  get textContentId() {
    return `comp-speak-text-${this.componentId}`;
  }

  get ariaRegionLabel() {
    return this.canSpeak ? "Text mit Sprachausgabe" : "Text";
  }

  // get speakButtonAriaLabel() {
  //   return `Text vorlesen: ${this.cleanText}`;
  // }

  // get stopButtonAriaLabel() {
  //   return "Vorlesen stoppen";
  // }

  get textAriaLabel() {
    return this.speaking ? `Wird vorgelesen: ${this.cleanText}` : this.cleanText;
  }

  get cleanText() {
    // Remove HTML tags for aria labels
    return this.text ? this.text.replace(/<[^>]*>/g, '') : '';
  }
}
